import { useMemo } from 'react';
import type { CategoryGroup } from '@trinity/chart-theming';
import { TRINITY_THEME } from '@trinity/chart-theming';
import { useDashboardConfig } from '@trinity/chat-components/containers/Dashboard/context';

/**
 * Returns a `CategoryGroup[]` shaped for `ChartThemePicker`.
 *
 * Always includes the built-in Trinity theme as the first category so it
 * appears in the picker even on pages with no user-created custom themes.
 * User-created palettes are grouped under a separate "My Themes" category.
 */
export function useDashboardThemeCategories(selectedThemeId: number | null): CategoryGroup[] {
  const { config } = useDashboardConfig();

  return useMemo<CategoryGroup[]>(() => {
    const builtInCategory: CategoryGroup = {
      category: { id: 0, name: 'Default' },
      items: [
        {
          id: TRINITY_THEME.id,
          name: TRINITY_THEME.name,
          colors: [...TRINITY_THEME.colors],
          editable: false,
        },
      ],
      paletteId: selectedThemeId ?? 0,
    };

    const customItems = (config.customThemes ?? []).map((t) => ({
      id: t.id,
      name: t.name,
      colors: t.colors,
      editable: false,
    }));

    const categories: CategoryGroup[] = [builtInCategory];

    if (customItems.length > 0) {
      categories.push({
        category: { id: 1, name: 'My Themes' },
        items: customItems,
        paletteId: selectedThemeId ?? 0,
      });
    }

    return categories;
  }, [config.customThemes, selectedThemeId]);
}
